import { C } from "@/lib/brand";

// ASCII syringe fill diagram. Scales the fill region to the requested
// `drawVolumeMl` / `syringeUnits` (a 1ml insulin syringe is 100 units).
// Renders inside a <pre> so it stays monospace + selectable. When the BE
// ships proper SVG/raster diagrams, callers can swap this for an <img>
// via the `imageUrl` prop without changing layout.
export default function SyringeFillDiagram({ drawVolumeMl, syringeUnits, imageUrl, capacityMl = 1 }) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt="Syringe fill"
        style={{ width: "100%", maxWidth: 320, display: "block" }}
        data-testid="syringe-fill-image"
      />
    );
  }

  // 40-char wide barrel — short enough to fit on a phone screen and read
  // comfortably at arm's length during injection prep.
  const BARREL = 40;
  const pct = Math.max(0, Math.min(1, (drawVolumeMl || 0) / capacityMl));
  const filled = Math.round(BARREL * pct);
  const empty = BARREL - filled;
  // Top of plunger sits at the boundary between fill and air.
  const plungerLine = "█".repeat(filled) + "·".repeat(empty);
  const barrelTop   = "┌" + "─".repeat(BARREL) + "┐";
  const barrelBot   = "└" + "─".repeat(BARREL) + "┘";
  // Tick marks every quarter of capacity.
  const tickRow = (() => {
    const cells = Array(BARREL).fill(" ");
    for (let i = 0; i <= 4; i++) {
      const x = Math.min(BARREL - 1, Math.round((i / 4) * (BARREL - 1)));
      cells[x] = "|";
    }
    return cells.join("");
  })();
  const labelRow = `0${" ".repeat(BARREL - 3)}${capacityMl}ml`;

  return (
    <div data-testid="syringe-fill-ascii">
      <pre
        style={{
          margin: 0,
          padding: 14,
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 11,
          lineHeight: 1.25,
          background: C.ink,
          color: C.peach,
          overflowX: "auto",
        }}
      >
{`        ${tickRow}
   ╔══╗ ${barrelTop}
NEEDLE${barrelTop.replace(/[┌┐]/g, "─")}
   ╚══╝ │${plungerLine}│◄ PLUNGER
        ${barrelBot}
        ${labelRow}`}
      </pre>
      <div style={{ marginTop: 10, fontSize: 12, color: C.warmGray, lineHeight: 1.55 }}>
        <strong style={{ color: C.ink }}>{drawVolumeMl}ml</strong>
        {typeof syringeUnits === "number" && (
          <> · <strong style={{ color: C.ink }}>{syringeUnits} units</strong> on the syringe scale</>
        )}
        {" "}— draw to the top of the plunger seal, not the rubber stopper.
      </div>
    </div>
  );
}
